
import { Injectable } from '@angular/core';
import { CanLoad, Router } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { PATIENT_PRE_APPROVAL_DONE } from 'src/app/guards/pre-approval.guard';
import { StateService } from 'src/app/services/state.service';
import { BrandService } from '../services/brand.service';
import { LoadingService } from '../services/loading.service';
import spinnerOptions from '../utils/spinner';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanLoad {

  productName: string;

  constructor(
    private router: Router,
    private loader: LoadingService,
    private brandService: BrandService,
    private stateService: StateService,
  ) {
    this.productName = this.brandService.getProductName();
  }

  async canLoad(): Promise<boolean> {
    const ATTEMPTS = 5;
    const DELAY = 700;

    // Return a new promise after a delay for the magic to work
    const delay = (t: any, v: any) => {
      return new Promise((resolve) => {
        setTimeout(resolve.bind(null, v), t);
      });
    };

    // The actual promise that checks the user session
    const getPromise = async () => {
      try {
        // Try force refresh first (get fresh tokens)
        return await fetchAuthSession({ forceRefresh: true });
      } catch (error) {
        console.warn('Force refresh failed, falling back to cached session:', error);

        try {
          // Fall back to cached session if refresh fails
          return await fetchAuthSession({ forceRefresh: false });
        } catch (fallbackError) {
          console.error('Failed to fetch auth session entirely:', fallbackError);
          return;
        }
      }
    };

    // The first attempts usually fail due to the way aws-amplify works,
    // The slower the network, the more attempts might be required
    // That's why we need to retry a few times
    // And exit after the first successful attempt
    const retry = async (maxAttempts: number = 5) => {
      // this.loader.start('Loading');
      try {
        for (let i = 0; i < maxAttempts; i++) {
          const attempt = await delay(DELAY, this).then(() => getPromise());
          if (attempt) {
            return attempt;
          }
        }
      } finally {
        // this.loader.stop();
      }
      return;
    };

    return new Promise((resolve, reject) => {
      this.loader.loadWhile(async stopLoading => {
        let loggedIn = false;
        try {
          const result: AuthSession | undefined = await retry(ATTEMPTS);
          if (!result?.tokens?.accessToken) {
            this.router.navigate(['unauthenticated']);
            reject();
          } else {
            loggedIn = true;
            const userPhoneNumber = result?.tokens?.idToken?.payload['phone_number'] as string;
            const isPatientPreApprovalDone = (await Preferences.get({ key: PATIENT_PRE_APPROVAL_DONE + userPhoneNumber }))?.value;
            if (isPatientPreApprovalDone) {
              resolve(true);
            } else {
              this.router.navigate(['preApproval']);
              reject();
            }
            return;
          }
        } catch (err) {
          reject(err);
        } finally {
          this.stateService.setLoggedInStatus(loggedIn);
          stopLoading();
        }
      }, spinnerOptions(`Loading ${this.productName}`));
    });
  }
}
